import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../../components/Button';
import { AddUserModal, type AddUserSubmission } from './AddUserModal';
import {
  ROLE_COACH,
  ROLE_ORG_ADMIN,
  roleKeyFromLabel,
  roleLabelFromKey,
} from './adminRoles';
import { SelectPopover } from '../../components/SelectPopover';
import '../../components/DataTable.css';

import { resolveBackTarget } from './adminNavigation';
import { useAuth } from '../../hooks/useAuth';
import {
  getOrganization,
  getOrganizationUsers,
  deleteOrganization,
  updateOrganization,
  createUserForOrganization,
  deleteUserFromOrganization,
  updateOrganizationUserRole,
} from '../../services/admin';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../services/firebase';
import type {
  OrganizationDetail as OrgDetail,
  AdminUser,
  PauseOrganizationResponse,
  OrganizationTrialResponse,
} from '../../types/admin';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ArrowLeftIcon } from '../../assets/ArrowLeftIcon';
import { OrganizationPausedBanner } from './OrganizationPausedBanner';
import { PauseOrganizationModal } from './PauseOrganizationModal';
import { ResumeOrganizationModal } from './ResumeOrganizationModal';
import { useOrganizationPause } from './useOrganizationPause';
import { OrganizationSampleDataPanel } from './OrganizationSampleDataPanel';
import { OrganizationTrialPanel } from './OrganizationTrialPanel';
import { AdminPage, AdminPageHeader, AdminPanel } from './AdminPage';
import { SetTrialModal } from './SetTrialModal';
import { ConvertTrialModal } from './ConvertTrialModal';
import { ClearTrialModal } from './ClearTrialModal';
import { hasTrialWindow } from './organizationTrial';
import { useOrganizationTrial } from './useOrganizationTrial';
import './OrganizationDetail.css';
import './OrganizationTrial.css';
import './OrganizationPause.css';

/**
 * OrganizationDetail displays details and users for a specific organization.
 * Access control is handled at the route level by ProtectedRoute.
 */
export const OrganizationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { switchOrganization } = useAuth();

  const [organization, setOrganization] = useState<OrgDetail | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create user form state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [roleUserId, setRoleUserId] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);

  // Delete confirmation state
  const [showDeleteOrgModal, setShowDeleteOrgModal] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState<AdminUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit organization form state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editSport, setEditSport] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Password reset state
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);

  const fetchOrganization = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const org = await getOrganization(id);
      setOrganization(org);
    } catch (err) {
      console.error('Failed to fetch organization:', err);
      setError('Failed to load organization details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchUsers = useCallback(async () => {
    if (!id) return;

    setUsersLoading(true);

    try {
      const userList = await getOrganizationUsers(id);
      setUsers(userList);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrganization();
    fetchUsers();
  }, [fetchOrganization, fetchUsers]);

  /** Applies a pause/unpause response to local state so no refetch is needed. */
  const handlePauseStateChanged = useCallback((result: PauseOrganizationResponse) => {
    setOrganization((current) =>
      current
        ? {
            ...current,
            isPaused: result.isPaused,
            pausedAt: result.pausedAt,
            pausedMessage: result.pausedMessage,
          }
        : current
    );
  }, []);

  const pause = useOrganizationPause({
    organizationId: id,
    onPauseStateChanged: handlePauseStateChanged,
  });

  /**
   * Applies a trial response to local state so no refetch is needed.
   *
   * All five fields are taken from the response rather than patched selectively: the status and
   * the day count are derived server-side, so recomputing either here is how the panel comes to
   * disagree with the row in the list.
   */
  const handleTrialChanged = useCallback((result: OrganizationTrialResponse) => {
    setOrganization((current) =>
      current
        ? {
            ...current,
            trialStartedAt: result.trialStartedAt,
            trialEndsAt: result.trialEndsAt,
            trialConvertedAt: result.trialConvertedAt,
            trialStatus: result.trialStatus,
            trialDaysRemaining: result.trialDaysRemaining,
          }
        : current
    );
  }, []);

  const trial = useOrganizationTrial({
    organizationId: id,
    onTrialChanged: handleTrialChanged,
  });

  /*
    Return to the list exactly as it was, page and search included. The list passes its own URL as
    route state; without that this navigated to a bare `/admin`, which discarded the query params
    and dropped an admin back on page 1 after looking at anything further in.

    Falls back to `/admin` for an arrival with no state — a pasted link, a refresh, or a redirect
    from elsewhere. Mirrors ContactDetailView.
  */
  const handleBack = () => {
    navigate(resolveBackTarget(location.state));
  };

  const handleSwitchToOrg = () => {
    if (organization) {
      switchOrganization({
        id: organization.id,
        name: organization.name,
        sport: organization.sport,
      });
      navigate('/players');
    }
  };

  const handleDeleteOrg = async () => {
    if (!id) return;

    setDeleteLoading(true);
    try {
      await deleteOrganization(id);
      // Deliberately not `from`: the row that link described is gone, and if it was the only one on
      // that page the page no longer exists. Back to the top of the list.
      navigate('/admin');
    } catch (err) {
      console.error('Failed to delete organization:', err);
      setError('Failed to delete organization.');
    } finally {
      setDeleteLoading(false);
      setShowDeleteOrgModal(false);
    }
  };

  const handleStartEdit = () => {
    if (organization) {
      setEditName(organization.name);
      setEditSport(organization.sport || '');
      setEditError(null);
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id || !editName.trim() || !editSport) return;

    setEditLoading(true);
    setEditError(null);

    try {
      await updateOrganization(id, {
        name: editName.trim(),
        sport: editSport || '',
      });

      // Refresh organization details
      fetchOrganization();
      setIsEditing(false);
    } catch (err: unknown) {
      console.error('Failed to update organization:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update organization.';
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCreateUser = async (submission: AddUserSubmission) => {
    if (!id) return;

    setCreateUserLoading(true);
    setCreateUserError(null);
    setTempPassword(null);

    try {
      const response = await createUserForOrganization(id, submission);

      // Closed on success, so the temporary password below is not hidden behind the dialog that
      // produced it — that password is the only time it is ever shown.
      setShowCreateUser(false);
      setTempPassword(response.temporaryPassword);
      fetchUsers();
    } catch (err: unknown) {
      console.error('Failed to create user:', err);
      // Left open with the message, so the entered details are not lost to a retry.
      setCreateUserError(err instanceof Error ? err.message : 'Failed to create user.');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleChangeRole = async (user: AdminUser, nextRole: string) => {
    if (!id || user.role === nextRole) return;

    setRoleUserId(user.id);
    setRoleError(null);

    try {
      await updateOrganizationUserRole(id, user.id, nextRole);
      fetchUsers();
    } catch (err: unknown) {
      // The API refuses to demote an organization's last administrator, and to touch a platform
      // account. Its message says which, so it is shown rather than replaced.
      setRoleError(
        err instanceof Error && err.message
          ? err.message
          : 'Failed to change that role.'
      );
    } finally {
      setRoleUserId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!id || !deleteUserTarget) return;

    setDeleteLoading(true);
    try {
      await deleteUserFromOrganization(id, deleteUserTarget.id);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError('Failed to delete user.');
    } finally {
      setDeleteLoading(false);
      setDeleteUserTarget(null);
    }
  };

  const handleSendPasswordReset = async (user: AdminUser) => {
    setResetPasswordUserId(user.id);
    setResetPasswordSuccess(null);

    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetPasswordSuccess(`Password reset email sent to ${user.email}`);

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setResetPasswordSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Failed to send password reset:', err);
      setError('Failed to send password reset email.');
    } finally {
      setResetPasswordUserId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <AdminPage className="org-detail">
        <p className="admin-state">Loading organization...</p>
      </AdminPage>
    );
  }

  if (error || !organization) {
    return (
      <AdminPage className="org-detail">
        <div className="admin-error" role="alert">
          {error || 'Organization not found.'}
          <button onClick={handleBack} className="org-detail__back-btn">
            Back to Organizations
          </button>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className="org-detail">
      <AdminPageHeader
        back={
          <button className="org-detail__back" onClick={handleBack}>
            <ArrowLeftIcon className="org-detail__back-icon" />
            Back to Organizations
          </button>
        }
        title={organization.name}
        meta={
          !isEditing && organization.sport ? (
            <span className={`org-detail__sport org-detail__sport--${organization.sport}`}>
              {organization.sport}
            </span>
          ) : undefined
        }
        actions={
          isEditing ? undefined : (
            <>
              {/*
                Billing setup reachable from the organization itself, not only from the billing
                section. Setting one up otherwise meant finding the organization here, going to
                /admin/billing, and finding it a second time.
              */}
              <Link
                className="org-detail__billing-btn"
                to={`/admin/billing/organizations/${organization.id}`}
                data-testid="org-detail-billing"
              >
                Billing setup
              </Link>
              {/* The per-organization view of Uploaded Rosters: the same list, filtered by URL. */}
              <Link
                className="org-detail__link-btn"
                to={`/admin/roster-uploads?org=${encodeURIComponent(organization.id)}`}
                data-testid="org-detail-roster-uploads"
              >
                View uploaded rosters
              </Link>
              <button
                className="org-detail__edit-btn"
                onClick={handleStartEdit}
              >
                Edit
              </button>
              <button
                className="org-pause-toggle-btn"
                onClick={() =>
                  organization.isPaused
                    ? pause.openUnpause()
                    : pause.openPause(organization.pausedMessage)
                }
                data-testid="org-pause-toggle"
              >
                {organization.isPaused ? 'Resume Organization' : 'Pause Organization'}
              </button>
              <button
                className="org-detail__switch-btn"
                onClick={handleSwitchToOrg}
              >
                Switch to this Organization
              </button>
              <button
                className="org-detail__delete-btn"
                onClick={() => setShowDeleteOrgModal(true)}
              >
                Delete Organization
              </button>
            </>
          )
        }
      />

      {isEditing && (
        <AdminPanel>
          <form className="org-detail__edit-form" onSubmit={handleSaveEdit}>
            <div className="org-detail__edit-row">
              <input
                type="text"
                className="org-detail__edit-input"
                placeholder="Organization Name *"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
              <select
                className="org-detail__edit-select"
                value={editSport}
                onChange={(e) => setEditSport(e.target.value)}
                required
              >
                <option value="">Select Sport *</option>
                <option value="baseball">Baseball</option>
                <option value="softball">Softball</option>
              </select>
              <button
                type="submit"
                className="org-detail__save-btn"
                disabled={editLoading || !editName.trim() || !editSport}
              >
                {editLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="org-detail__cancel-btn"
                onClick={handleCancelEdit}
                disabled={editLoading}
              >
                Cancel
              </button>
            </div>
            {editError && (
              <div className="org-detail__edit-error">{editError}</div>
            )}
          </form>
        </AdminPanel>
      )}

      {organization.isPaused && (
        <OrganizationPausedBanner
          pausedAt={organization.pausedAt}
          pausedMessage={organization.pausedMessage}
        />
      )}

      <OrganizationTrialPanel
        organization={organization}
        onSetDates={() => trial.openDates(organization)}
        onConvert={trial.openConvert}
        onClear={trial.openClear}
      />

      <div className="org-detail__stats">
        <div className="org-detail__stat">
          <span className="org-detail__stat-value">{organization.stats.totalUsers}</span>
          <span className="org-detail__stat-label">Users</span>
        </div>
        <div className="org-detail__stat">
          <span className="org-detail__stat-value">{organization.stats.totalProspects}</span>
          <span className="org-detail__stat-label">Prospects</span>
        </div>
        <div className="org-detail__stat">
          <span className="org-detail__stat-value">{organization.stats.totalTeams}</span>
          <span className="org-detail__stat-label">Teams</span>
        </div>
        <div className="org-detail__stat">
          <span className="org-detail__stat-value">{formatDate(organization.stats.lastLoginAt)}</span>
          <span className="org-detail__stat-label">Last Login</span>
        </div>
      </div>

      <AdminPanel
        title="Users"
        actions={
          <Button variant="primary" onClick={() => setShowCreateUser(true)}>
            Add User
          </Button>
        }
      >
        <AddUserModal
          isOpen={showCreateUser}
          organizationName={organization.name}
          onClose={() => {
            setShowCreateUser(false);
            setCreateUserError(null);
          }}
          onSubmit={handleCreateUser}
          busy={createUserLoading}
          error={createUserError}
        />

        {tempPassword && (
          <div className="org-detail__temp-password">
            <strong>Temporary password:</strong> <code>{tempPassword}</code>
            <small>
              Share this securely with the new user. They should change it after
              first login. It is not shown again.
            </small>
          </div>
        )}

        {roleError && (
          <div className="org-detail__reset-success org-detail__role-error" role="alert">
            {roleError}
          </div>
        )}

        {resetPasswordSuccess && (
          <div className="org-detail__reset-success">
            {resetPasswordSuccess}
          </div>
        )}

        {usersLoading ? (
          <p className="admin-state">Loading users...</p>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="data-table__empty">
                      No users in this organization
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td className="data-table__muted">
                        {user.firstName || user.lastName
                          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                          : '-'}
                      </td>
                      <td>
                        {/*
                          A platform account's role is not an organization's page to change, so it
                          reads as a badge here rather than a control that would be refused.
                        */}
                        {user.role === 'system_admin' ? (
                          <span className="data-table__badge data-table__badge--primary">
                            {roleLabelFromKey(user.role)}
                          </span>
                        ) : (
                          <SelectPopover
                            options={[ROLE_COACH, ROLE_ORG_ADMIN]}
                            value={roleLabelFromKey(user.role)}
                            onChange={(label) =>
                              handleChangeRole(user, roleKeyFromLabel(label))
                            }
                            placeholder={ROLE_COACH}
                            clearable={false}
                            disabled={roleUserId === user.id}
                          />
                        )}
                      </td>
                      <td className="data-table__muted">{formatDate(user.createdAt)}</td>
                      <td className="data-table__muted">{formatDate(user.lastLoginAt)}</td>
                      <td className="data-table__actions">
                        <button
                          className="org-detail__user-reset"
                          onClick={() => handleSendPasswordReset(user)}
                          disabled={resetPasswordUserId === user.id}
                        >
                          {resetPasswordUserId === user.id ? 'Sending...' : 'Reset Password'}
                        </button>
                        <button
                          className="org-detail__user-delete"
                          onClick={() => setDeleteUserTarget(user)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </AdminPanel>

      {/* Keyed so moving to another organization never shows the previous one's sample data. */}
      <OrganizationSampleDataPanel key={organization.id} organizationId={organization.id} />
      <SetTrialModal
        isOpen={trial.modal === 'dates'}
        organizationName={organization.name}
        isEditing={hasTrialWindow(organization)}
        startDate={trial.startDate}
        endDate={trial.endDate}
        onStartDateChange={trial.setStartDate}
        onEndDateChange={trial.setEndDate}
        loading={trial.loading}
        error={trial.error}
        errorTarget={trial.errorTarget}
        errorField={trial.errorField}
        errorSeq={trial.errorSeq}
        onConfirm={trial.confirmDates}
        onClose={trial.close}
      />

      <ConvertTrialModal
        isOpen={trial.modal === 'convert'}
        organizationName={organization.name}
        loading={trial.loading}
        error={trial.error}
        onConfirm={trial.confirmConvert}
        onClose={trial.close}
      />

      <ClearTrialModal
        isOpen={trial.modal === 'clear'}
        organizationName={organization.name}
        loading={trial.loading}
        error={trial.error}
        onConfirm={trial.confirmClear}
        onClose={trial.close}
      />

      <PauseOrganizationModal
        isOpen={pause.modal === 'pause'}
        organizationName={organization.name}
        message={pause.message}
        onMessageChange={pause.setMessage}
        loading={pause.loading}
        error={pause.error}
        onConfirm={pause.confirmPause}
        onClose={pause.close}
      />

      <ResumeOrganizationModal
        isOpen={pause.modal === 'unpause'}
        organizationName={organization.name}
        loading={pause.loading}
        error={pause.error}
        onConfirm={pause.confirmUnpause}
        onClose={pause.close}
      />

      <ConfirmModal
        isOpen={showDeleteOrgModal}
        title="Delete Organization"
        message={`Are you sure you want to delete "${organization.name}"? This action cannot be undone.`}
        confirmText={deleteLoading ? "Deleting..." : "Delete"}
        busy={deleteLoading}
        onConfirm={handleDeleteOrg}
        onClose={() => setShowDeleteOrgModal(false)}
      />

      <ConfirmModal
        isOpen={deleteUserTarget !== null}
        title="Delete User"
        message={`Are you sure you want to delete "${deleteUserTarget?.email}"? This will remove them from Firebase and they will no longer be able to log in.`}
        confirmText={deleteLoading ? "Deleting..." : "Delete"}
        busy={deleteLoading}
        onConfirm={handleDeleteUser}
        onClose={() => setDeleteUserTarget(null)}
      />
    </AdminPage>
  );
};
